import { ClockIcon } from '@heroicons/react/24/outline';

import usePreferencesQuery from 'api/queries/usePreferencesQuery';
import RecurringIcon from 'honeydew/icons/FoodPreferences/RecurringIcon';
import useGlobalStore from 'hooks/useGlobalStore';

import { BackInStockWaitlistOptionButton } from './BackInStockWaitlistOptionButton';
import useWaitlistedItemIdsQuery from './useWaitlistedItemIdsQuery';

/**
 * Alert module shown on the PDP when a product is sold out.
 * Opens the back-in-stock waitlist modal so users can join the waitlist and/or add to recurring.
 *
 * @param {Object} props
 * @param {boolean} [props.isAutoshipCustomer=false] - Whether the customer is on an autoship plan
 * @param {import('features/shop/ProductCardV2/types').ProductData} props.product - The sold-out product
 */
export function SoldOutBackInStockWaitlistAlert({ isAutoshipCustomer = false, product }) {
    const { data: preferences = {} } = usePreferencesQuery();
    const { data: waitlistedItemIds = [] } = useWaitlistedItemIdsQuery();
    const { setBackInStockWaitlistModal } = useGlobalStore();

    const recurringItemExternalSkus = new Set(
        preferences.recurring?.[0]?.recurring?.map((item) => item?.id).filter(Boolean) ?? []
    );
    const isInRecurringList = product.externalSku ? recurringItemExternalSkus.has(product.externalSku) : false;
    const isOnWaitlist = waitlistedItemIds.includes(product.id);

    const openBackInStockWaitlistModal = () => {
        setBackInStockWaitlistModal({
            open: true,
            productData: product,
            source: 'pdp',
        });
    };

    return (
        <div className="rounded-md border border-info bg-info/5 p-5 lg:p-10" role="alert">
            <h2 className="font-grotesk-bold text-body-mobile-md md:text-body-desktop-sm">Whew, that sold out fast!</h2>
            <p className="mt-10 text-body-mobile-xs md:text-body-mobile-sm">
                Get first dibs when it&apos;s back in stock:
            </p>
            <div className="mt-10 flex flex-col gap-10 md:flex-row">
                <BackInStockWaitlistOptionButton
                    active={isOnWaitlist}
                    activeLabel="On the Waitlist"
                    className="text-body-sm basis-0"
                    label="Join the Waitlist"
                    onClick={openBackInStockWaitlistModal}
                >
                    <ClockIcon className="size-[20px] shrink-0 stroke-[2px]" />
                </BackInStockWaitlistOptionButton>
                {isAutoshipCustomer && !product.isBundle && (
                    <BackInStockWaitlistOptionButton
                        active={isInRecurringList}
                        activeLabel="Added to Recurring"
                        className="text-body-sm basis-0"
                        label="Add to Recurring"
                        onClick={openBackInStockWaitlistModal}
                    >
                        <RecurringIcon width={20} />
                    </BackInStockWaitlistOptionButton>
                )}
            </div>
        </div>
    );
}
